import { useState, useCallback } from 'react';
import { Check, Droplets, FlaskConical } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { householdCompounds, householdCompoundColors } from '@/config/experiments/chem1131/experiment1.config';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Adding Water to Solid Samples',
    content: `The solubility test is often the first step in qualitative analysis because it immediately divides compounds into two groups.

**Why 5 mL of Water?**
- Provides enough solvent to dissolve most soluble compounds
- Creates a consistent liquid:solid ratio for comparison
- Leaves room for mixing without overflow

**Using Deionized (DI) Water:**
- DI water has no dissolved minerals that could interfere with tests
- Tap water contains ions (Ca²⁺, Mg²⁺, Cl⁻) that may cause false reactions
- Always use DI water for analytical chemistry

**Temperature Matters:**
Room temperature water (~20°C) is standard. Hot water would dissolve more material, potentially giving misleading results.`
  },
  2: {
    title: 'Mixing Technique for Solubility',
    content: `Proper mixing ensures maximum contact between solid and solvent.

**Mixing Methods:**
1. **Gentle Swirling**: Rotate the tube in small circles
2. **Vortex Mixing**: Use a vortex mixer for consistent results
3. **Glass Rod Stirring**: For stubborn solids (avoid scratching tube)

**What to Observe:**
- Does the solid disappear completely? → Soluble
- Does it remain as particles/cloudy suspension? → Insoluble
- Does it partially dissolve? → Slightly soluble

**Time Factor:**
Allow 1-2 minutes of mixing. Some compounds dissolve slowly. If solid remains after thorough mixing, classify as insoluble.`
  },
  3: {
    title: 'Recording Solubility Results',
    content: `Accurate recording is essential for the identification flowchart.

**Soluble Compounds (7 total):**
These dissolve to give clear solutions:
- NaCl, NaHCO₃, Na₂CO₃, NaOH
- MgSO₄, H₃BO₃, Sucrose

**Insoluble Compounds (3 total):**
These remain as visible particles:
- CaCO₃ (Calcium carbonate) - chalk
- CaSO₄ (Calcium sulfate) - plite of Paris
- Corn starch - white powder

**Why the Difference?**
Solubility depends on the strength of solute-solvent interactions vs. crystal lattice energy. Ionic compounds with small, highly charged ions generally dissolve well in polar water.`
  }
};

interface TubeState {
  hasWater: boolean;
  mixed: boolean;
  soluble: boolean;
}

export default function Part2Solubility() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [waterAdded, setWaterAdded] = useState(false);
  const [tubesMixed, setTubesMixed] = useState(false);
  const [solubilityRecorded, setSolubilityRecorded] = useState(false);
  const [tubeStates, setTubeStates] = useState<Record<string, TubeState>>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTube, setSelectedTube] = useState<string | null>(null);

  const compounds = Object.values(householdCompounds);
  const completedSteps = [waterAdded, tubesMixed, solubilityRecorded].filter(Boolean).length;

  const handleAddWater = useCallback(() => {
    if (!waterAdded) {
      // Add water to all tubes
      const newStates: Record<string, TubeState> = {};
      compounds.forEach(compound => {
        newStates[compound.id] = {
          hasWater: true,
          mixed: false,
          soluble: compound.soluble
        };
      });
      setTubeStates(newStates);
      setWaterAdded(true);
      setCurrentStep(2);
    }
  }, [waterAdded, compounds]);

  const handleMixTube = useCallback((compoundId: string) => {
    if (waterAdded && !tubesMixed) {
      setTubeStates(prev => {
        const updated = { ...prev };
        updated[compoundId] = { ...updated[compoundId], mixed: true };

        // Check if all tubes are mixed
        const allMixed = compounds.every(c => updated[c.id]?.mixed);
        if (allMixed) {
          setTubesMixed(true);
          setCurrentStep(3);
        }
        return updated;
      });
    }
  }, [waterAdded, tubesMixed, compounds]);

  const handleRecordResults = useCallback(() => {
    if (tubesMixed && !solubilityRecorded) {
      setSolubilityRecorded(true);
    }
  }, [tubesMixed, solubilityRecorded]);

  const getSolutionColor = (compound: typeof compounds[0], state?: TubeState) => {
    if (!state?.hasWater) return 'transparent';
    if (!state.mixed) return householdCompoundColors.clearWater;
    return compound.soluble ? householdCompoundColors.dissolved : householdCompoundColors.cloudySuspension;
  };

  const solubleCompounds = compounds.filter(c => c.soluble);
  const insolubleCompounds = compounds.filter(c => !c.soluble);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part II: Solubility Test</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Add Water', hint: 'Add 5 mL water to each tube', done: waterAdded },
            { id: 2, title: 'Mix Solutions', hint: 'Shake each tube to mix', done: tubesMixed },
            { id: 3, title: 'Record Results', hint: 'Note soluble vs insoluble', done: solubilityRecorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Results Summary */}
        {tubesMixed && (
          <div className="results-summary">
            <h4>Solubility Results</h4>
            <div className="result-group">
              <span className="result-label soluble">Soluble (7):</span>
              <div className="compound-tags">
                {solubleCompounds.map(c => (
                  <span key={c.id} className="tag soluble">{c.formula}</span>
                ))}
              </div>
            </div>
            <div className="result-group">
              <span className="result-label insoluble">Insoluble (3):</span>
              <div className="compound-tags">
                {insolubleCompounds.map(c => (
                  <span key={c.id} className="tag insoluble">{c.formula}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Solubility Testing"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Deionized water', 'Wash bottle', 'Stirring rod']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="solubility-workspace">
          {/* Water Dispenser */}
          <div className="water-dispenser">
            <div
              className={`dispenser ${currentStep === 1 && !waterAdded ? 'clickable' : ''}`}
              onClick={handleAddWater}
            >
              <svg viewBox="0 0 60 100" width={80} height={120}>
                <rect x="15" y="20" width="30" height="65" rx="5" fill="#4A90A4" />
                <rect x="20" y="30" width="20" height="45" fill="#7DD3FC" opacity="0.7" />
                <rect x="22" y="10" width="16" height="15" rx="2" fill="#333" />
                <path d="M30 85 L25 95 L35 95 Z" fill="#7DD3FC" />
              </svg>
              <span className="dispenser-label">DI Water</span>
            </div>
          </div>

          {/* Test Tube Rack with Tubes */}
          <div className="tube-rack">
            {compounds.map((compound, index) => {
              const state = tubeStates[compound.id];
              const mixedCount = compounds.filter(c => tubeStates[c.id]?.mixed).length;

              return (
                <div
                  key={compound.id}
                  className={`tube-container ${
                    currentStep === 2 && state?.hasWater && !state?.mixed ? 'clickable' : ''
                  } ${selectedTube === compound.id ? 'selected' : ''}`}
                  onClick={() => {
                    if (currentStep === 2 && state?.hasWater && !state?.mixed) {
                      handleMixTube(compound.id);
                    }
                    setSelectedTube(compound.id);
                  }}
                >
                  <svg viewBox="0 0 40 100" width={40} height={100}>
                    {/* Test tube */}
                    <rect x="8" y="5" width="24" height="70" rx="2" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="1.5" />
                    <ellipse cx="20" cy="75" rx="12" ry="5" fill="rgba(200,220,240,0.4)" stroke="#7A9BB8" strokeWidth="1" />

                    {/* Solid compound at bottom (if not yet mixed or insoluble) */}
                    {state?.hasWater && !compound.soluble && (
                      <ellipse cx="20" cy="68" rx="10" ry="4" fill="#F5F5F5" opacity="0.9" />
                    )}
                    {state?.hasWater && !state.mixed && compound.soluble && (
                      <ellipse cx="20" cy="68" rx="10" ry="4" fill="#F5F5F5" opacity="0.9" />
                    )}

                    {/* Solution */}
                    {state?.hasWater && (
                      <rect
                        x="10"
                        y="25"
                        width="20"
                        height="45"
                        fill={getSolutionColor(compound, state)}
                        rx="1"
                      />
                    )}

                    {/* Mixing animation */}
                    {state?.hasWater && !state?.mixed && currentStep === 2 && (
                      <g className="mix-indicator">
                        <circle cx="20" cy="45" r="3" fill="#60A5FA">
                          <animate attributeName="cy" values="45;35;55;45" dur="1s" repeatCount="indefinite" />
                        </circle>
                      </g>
                    )}

                    {/* Cloudiness for insoluble after mixing */}
                    {state?.mixed && !compound.soluble && (
                      <g opacity="0.6">
                        <circle cx="15" cy="40" r="2" fill="#999" />
                        <circle cx="25" cy="50" r="2" fill="#AAA" />
                        <circle cx="18" cy="55" r="1.5" fill="#888" />
                        <circle cx="22" cy="45" r="1.5" fill="#999" />
                      </g>
                    )}

                    {/* Label */}
                    <rect x="10" y="10" width="20" height="12" fill="#FFFACD" stroke="#DAA520" strokeWidth="0.5" rx="1" />
                    <text x="20" y="19" fontSize="6" fill="#333" textAnchor="middle" fontWeight="bold">
                      {index + 1}
                    </text>
                  </svg>
                  <span className="tube-formula">{compound.formula}</span>
                  {state?.mixed && (
                    <span className={`solubility-badge ${compound.soluble ? 'soluble' : 'insoluble'}`}>
                      {compound.soluble ? 'S' : 'I'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected Compound Info */}
          {selectedTube && (
            <div className="compound-info">
              <strong>{householdCompounds[selectedTube]?.name}</strong>
              <span>({householdCompounds[selectedTube]?.commonName})</span>
              {tubeStates[selectedTube]?.mixed && (
                <span className={`status ${householdCompounds[selectedTube]?.soluble ? 'soluble' : 'insoluble'}`}>
                  {householdCompounds[selectedTube]?.soluble ? 'Dissolved - Clear solution' : 'Did not dissolve - Suspension'}
                </span>
              )}
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {!waterAdded && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the water dispenser to add 5 mL water to all tubes
              </div>
            )}
            {waterAdded && !tubesMixed && (
              <div className="status-message info">
                <Droplets size={20} />
                Click each tube to shake and mix ({compounds.filter(c => tubeStates[c.id]?.mixed).length}/{compounds.length} mixed)
              </div>
            )}
            {tubesMixed && !solubilityRecorded && (
              <div className="status-message info" onClick={handleRecordResults} style={{ cursor: 'pointer' }}>
                <FlaskConical size={20} />
                Click here to record your solubility observations
              </div>
            )}
            {solubilityRecorded && (
              <div className="status-message success">
                <Check size={20} />
                Solubility test complete! 7 soluble, 3 insoluble compounds identified.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Solubility</h4>
            <p>
              Solubility depends on molecular structure. Ionic compounds with small, highly charged ions
              (like Na<sup>+</sup>, Cl<sup>-</sup>) are generally soluble. Compounds with low charge density
              or covalent character (like CaSO<sub>4</sub>, starch) are less soluble.
            </p>
          </div>
        </div>

        <style>{`
          .solubility-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .water-dispenser {
            display: flex;
            justify-content: center;
          }

          .dispenser {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .dispenser.clickable {
            cursor: pointer;
          }

          .dispenser.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .dispenser-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .tube-rack {
            display: flex;
            gap: 6px;
            background: linear-gradient(180deg, #5C534A 0%, #3D352F 100%);
            padding: 15px 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            flex-wrap: wrap;
            justify-content: center;
          }

          .tube-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            padding: 5px;
            border-radius: 4px;
            transition: all 0.2s;
            position: relative;
          }

          .tube-container.clickable {
            cursor: pointer;
          }

          .tube-container.clickable:hover {
            background: rgba(255,255,255,0.1);
          }

          .tube-container.selected {
            background: rgba(0, 168, 107, 0.2);
          }

          .tube-formula {
            font-size: 8px;
            color: #CCC;
            max-width: 40px;
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .solubility-badge {
            position: absolute;
            top: 2px;
            right: 2px;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            font-size: 9px;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .solubility-badge.soluble {
            background: var(--secondary);
            color: white;
          }

          .solubility-badge.insoluble {
            background: #F59E0B;
            color: white;
          }

          .compound-info {
            background: var(--bg-elevated);
            padding: 12px 20px;
            border-radius: 8px;
            display: flex;
            gap: 8px;
            align-items: center;
            flex-wrap: wrap;
            justify-content: center;
          }

          .compound-info strong {
            color: var(--text-primary);
          }

          .compound-info span {
            color: var(--text-secondary);
            font-size: 13px;
          }

          .compound-info .status {
            padding: 4px 10px;
            border-radius: 4px;
            font-weight: 500;
          }

          .compound-info .status.soluble {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .compound-info .status.insoluble {
            background: rgba(245, 158, 11, 0.2);
            color: #F59E0B;
          }

          .results-summary {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .results-summary h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .result-group {
            margin-bottom: 8px;
          }

          .result-label {
            font-size: 11px;
            font-weight: 600;
            display: block;
            margin-bottom: 4px;
          }

          .result-label.soluble { color: var(--secondary); }
          .result-label.insoluble { color: #F59E0B; }

          .compound-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          }

          .tag {
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 3px;
          }

          .tag.soluble {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .tag.insoluble {
            background: rgba(245, 158, 11, 0.2);
            color: #F59E0B;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp1"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
